'use client';

import React, { useEffect, useRef } from 'react';

import { MdsIcCloseCross } from '@groww-tech-pvt/icon-store/mint-icons';
import ReactDOM from 'react-dom';

interface ModalPortalProps {
  modalTitle?: string;
  modalTitleIcon?: React.ReactNode;
  titleClassName?: string;
  canClose?: boolean;
  setIsOpen: (isOpen: boolean) => void;
  className?: string;
  children?: React.ReactNode;
}

const ModalPortal = ({
  modalTitle = '',
  modalTitleIcon,
  titleClassName = 'displayBase',
  canClose = true,
  setIsOpen,
  className,
  children,
}: ModalPortalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    if (!canClose) return;
    setIsOpen(false);
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [canClose, setIsOpen]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      handleClose();
    }
  };

  // Ensure the modal root exists
  const modalRoot =
    typeof window !== 'undefined'
      ? (document.getElementById('modal-root') ?? document.body)
      : null;

  const modalContent = (
    <div
      className='fixed inset-0 z-9999 flex items-end sm:items-center justify-center bg-overlayPrimary'
      onClick={handleBackdropClick}
      role='dialog'
      aria-modal='true'
    >
      <div
        ref={modalRef}
        className={`relative p-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] w-screen sm:w-full sm:max-w-lg max-h-[90vh] overflow-y-auto glassBorder2 shadow-lg flex flex-col gap-1 bg-primary rounded-t-2xl sm:rounded-2xl ${className ?? ''}`}
      >
        <div className='flex items-center justify-between'>
          <p className={`contentPrimary ${titleClassName} flex gap-3 items-center`}>
              { React.isValidElement(modalTitleIcon) &&
                React.cloneElement(modalTitleIcon as React.ReactElement, {
                  size: 24,
                  className: 'contentPrimary',
                })}
            {modalTitle}
          </p>
          {canClose && (
            <button onClick={handleClose} aria-label='Close modal' className='cursor-pointer'>
             <MdsIcCloseCross size={24} className='contentPrimary' />
            </button>
          )}
        </div>
        <div>{children}</div>
      </div>
    </div>
  );

  if (!modalRoot) return null;
  return ReactDOM.createPortal(modalContent, modalRoot);
};

export default ModalPortal;
